import { useState, useEffect, useRef } from 'react';
import { MarkerType } from 'reactflow';
import dagre from 'dagre';

export default function useCreditPlanFlowNodes(tree, rootId = 'start', onHeaderClick = null, updateTrigger = 0, selectedNodeId = null) {
  // Remove expanded state logic
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [revision, setRevision] = useState(0);

  /* store user-dragged positions so Dagre won't override them */
  const frozenPos = useRef({});          // { nodeId: {x, y} }

  /* ────── Dagre config ────── */
  const W = 180, H = 60;

  const layout = (visNodes, visEdges) => {
    const g = new dagre.graphlib.Graph();   // new each time
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({
      rankdir: 'TB',
      nodesep: 500,   // ⬅︎ horizontal gap
      ranksep: 200,  // ⬅︎ vertical gap
    });
    visNodes.forEach((n) => g.setNode(n.id, { width: W, height: H }));
    visEdges.forEach((e) => g.setEdge(e.source, e.target));
    dagre.layout(g);

    return Object.fromEntries(
      visNodes.map((n) => {
        const { x, y } = g.node(n.id);
        return [n.id, { x: x - W / 2, y: y - H / 2 }];
      })
    );
  };

  useEffect(() => {
    // Check if tree is empty or undefined
    if (!tree || Object.keys(tree).length === 0) {
      console.log('Tree is empty, setting empty nodes and edges');
      setNodes([]);
      setEdges([]);
      setRevision((r) => r + 1);
      return;
    }

    const nArr = [];
    const eArr = [];
    const edgeSet = new Set(); // Track unique edges to prevent duplicates

    function walk(id, visited = new Set()) {
      if (visited.has(id)) return;
      visited.add(id);
      const entry = tree[id];
      
      // Check if entry exists and has required properties
      if (!entry) {
        console.warn(`Node ${id} not found in tree:`, tree);
        return;
      }
      
      // All nodes are visible, no expandable/expanded logic
      console.log(`Creating node ${id} with status:`, entry.status);
      nArr.push({
        id,
        type: 'expandable', // keep type for compatibility
        hidden: false,
        data: {
          id,
          label: entry.label || '',
          details: entry.details || '',
          premium: entry.premium || false,
          status: entry.status || 'standby', // Include status in node data with default
          deadline: entry.deadline || '',
          isSelected: id === selectedNodeId, // Add selected state
          // Remove expandable/expanded/onToggle
          onHeaderClick: onHeaderClick,
        },
        position: frozenPos.current[id] || { x: 0, y: 0 }, // temp
      });

      // Check if children exist and is an array
      if (!entry.children || !Array.isArray(entry.children)) {
        console.warn(`Node ${id} has no children or children is not an array:`, entry.children);
        return;
      }

      entry.children.forEach((child) => {
        // Create unique edge key to prevent duplicates
        const edgeKey = `${id}-${child}`;
        if (!edgeSet.has(edgeKey)) {
          edgeSet.add(edgeKey);
          eArr.push({
            id: edgeKey, // Use edgeKey as ID to ensure uniqueness
            source: id,
            target: child,
            hidden: false,
            markerEnd: { 
              type: MarkerType.ArrowClosed,
              color: '#000000',
            },
            style: { 
              strokeWidth: 3,
              stroke: '#000000',
            },
          });
        }
        walk(child);
      });
    }

    // Check if rootId exists in tree
    if (!tree[rootId]) {
      console.warn(`Root ID ${rootId} not found in tree:`, tree);
      console.log('Available tree keys:', Object.keys(tree));
      
      // Try to find a valid root ID
      const availableKeys = Object.keys(tree);
      if (availableKeys.length > 0) {
        const newRootId = availableKeys[0];
        console.log(`Using ${newRootId} as root ID instead of ${rootId}`);
        walk(newRootId);
      } else {
        setNodes([]);
        setEdges([]);
        setRevision((r) => r + 1);
        return;
      }
    } else {
      walk(rootId);
    }

    const visNodes = nArr.filter((n) => !n.hidden && !frozenPos.current[n.id]);
    const positions = layout(visNodes, eArr.filter((e) => !e.hidden));

    const finalNodes = nArr.map((n) => ({
      ...n,
      position:
        frozenPos.current[n.id]        // user-dragged
        ?? positions[n.id]             // auto-layout
        ?? n.position,                 // fallback
    }));

    setNodes(finalNodes);
    setEdges(eArr);
    setRevision((r) => r + 1);   
  }, [tree, rootId, onHeaderClick, updateTrigger, selectedNodeId]);

  /* expose a way to freeze a node after user drags it */
  const freezePosition = (id, position) => {
    frozenPos.current[id] = position;
  };

  const updatePositions = (changes) => {
    const moved = {};

    changes.forEach((c) => {
      if (c.type === 'position' && c.position) {
        moved[c.id] = c.position;          // collect valid moves only
        freezePosition(c.id, c.position);  // remember permanently
      }
    });

    if (Object.keys(moved).length) {
      setNodes((nds) =>
        nds.map((n) =>
          moved[n.id] ? { ...n, position: moved[n.id] } : n
        )
      );
    }
  };

  return { nodes, edges, freezePosition, revision, updatePositions };
}
